import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  inject,
  input,
  OnInit,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ValidationErrors } from '@angular/forms';
import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';

import { LocationConfigService } from '@spaces-ui/location';

const ERROR_MAP = [
  'Invalid number',
  'Invalid country code',
  'Too short',
  'Too long',
  'Invalid number',
  'Invalid number',
  'Must be US number',
];

@Component({
  selector: 'mdw-phone-number-input',
  templateUrl: './phone-number-input.html',
  styleUrls: ['./phone-number-input.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule],
})
export class MdwPhoneNumberInput implements AfterViewInit, OnInit, ControlValueAccessor {
  public ngControl = inject(NgControl, { self: true });
  private locationService = inject(LocationConfigService);

  public showErrors = input<boolean>(true);
  public showCountries = input<boolean>(true);
  public placeholder = input<string>();
  // This is a workaround for the fact that the intl-tel-input library
  // requires the library to be loaded onto a DOM element before we
  // can use their validation methods. Because of this, no passed in
  // angular validators like required will work.
  public requireValue = input<boolean>(true);

  public inputElementRef = viewChild<ElementRef<HTMLInputElement>>('telInputRef');

  @HostBinding('class.disabled')
  public isDisabled = false;
  public isTouched = false;

  public telPlugin?: Iti;
  public errorMessage?: string;

  public set value(val: string | undefined) {
    if (val !== this.val) {
      this.val = val;
    }
  }
  public get value() {
    return this.val;
  }
  private val?: string;

  private isFirstValidation = true;

  public constructor() {
    this.ngControl.valueAccessor = this;
  }

  public ngOnInit() {
    if (this.ngControl.control) {
      this.ngControl.control.setValidators([this.validate.bind(this)]);
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
  }

  public ngAfterViewInit() {
    const inputElRef = this.inputElementRef();

    if (!inputElRef) {
      return;
    }

    const nativeEl = inputElRef.nativeElement;
    nativeEl.addEventListener('countrychange', () => this.handleUpdateValue(this.value ?? ''));

    let initialCountry: CountryCode = 'US';

    try {
      const parsedCountry = this.value ? parsePhoneNumberWithError(this.value).country : undefined;
      if (parsedCountry) {
        initialCountry = parsedCountry;
      }
      // eslint-disable-next-line no-empty,no-empty-function,@typescript-eslint/no-empty-function
    } catch (e) {}

    const telOptions: SomeOptions = {
      loadUtilsOnInit: `${this.locationService.assetsLocation}/intl-tel-input/utils.js`,
      allowDropdown: this.showCountries(),
      initialCountry,
      dropdownContainer: document.body,
    };
    this.telPlugin = intlTelInput(nativeEl, telOptions);

    // run validation again after library initializes
    this.telPlugin.promise.then(() => this.validate());
  }

  public writeValue(value?: string) {
    this.value = value;
  }

  public registerOnChange(fn: () => unknown) {
    this.propagateChange = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public registerOnTouched() {}

  public handleUpdateValue(updatedNumber: string) {
    // const value = this.transformNumber();
    this.writeValue(updatedNumber);

    const apiNumber = this.transformNumber();
    this.propagateChange(apiNumber);
  }

  public handleFocus() {
    this.isTouched = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private propagateChange = (_: string | undefined) => {};

  private transformNumber() {
    const newNumber = this.telPlugin?.getNumber();

    return newNumber;
  }

  private validate(): ValidationErrors | null {
    const hasValue = this.value && this.value.length > 0;

    if (!this.telPlugin) {
      return null;
    }

    const isValid = this.telPlugin.isValidNumber();
    const errorCode = this.telPlugin.getValidationError();

    let errors: ValidationErrors | null = null;
    if ((isValid && hasValue) || (!this.requireValue() && !hasValue)) {
      this.errorMessage = undefined;
    } else {
      this.errorMessage = ERROR_MAP[errorCode] || ERROR_MAP[0];
      errors = {
        invalid: true,
        error: this.errorMessage,
      };
    }

    const inputElement = this.inputElementRef();
    if (this.isFirstValidation && errors && this.ngControl.control && inputElement) {
      // This is the only way to emit a validity status change
      // without emitting a value change
      if (!this.ngControl.control.touched) {
        this.ngControl.control.setErrors(errors);
        // this.ngControl.control.markAsTouched();
        // this.ngControl.control.markAsDirty();
        // inputElement.nativeElement.focus();

        // setTimeout(() => {
        //   inputElement.nativeElement.blur();
        // });
      }
    }

    this.isFirstValidation = false;

    return errors;
  }
}
